import React from 'react';
import { Button } from 'carbon-components-react';
import {
  AreaCustom16,
  FlowData16,
  RadioButton16,
  Maximize16,
  TrashCan16
} from '@carbon/icons-react';
import MapLocationSearch from 'components/MapLocationSearchV2';
import { LandParcelType } from 'types';
import { DrawingTool } from '../hooks/useMemberLookupMap';

const getToolInstruction = (tool: DrawingTool): string => {
  switch (tool) {
    case 'circle':
      return 'Click and drag to draw then release to finish';
    case 'polyline':
      return 'Double-click to finish line';
    case 'polygon':
      return 'Double-click to close shape';
    default:
      return 'Draw your project area';
  }
};

interface MemberLookupMapPanelProps {
  isReady: boolean;
  activeTool: DrawingTool;
  toolExpanded: boolean;
  hasDrawnShapes: boolean;
  onExpandTools: () => void;
  onToolClick: (tool: DrawingTool) => void;
  onClearAll: () => void;
  onUseLandParcel: (parcel: LandParcelType) => void;
  onCancelLandParcel: () => void;
}

const MemberLookupMapPanel: React.FC<MemberLookupMapPanelProps> = ({
  isReady,
  activeTool,
  toolExpanded,
  hasDrawnShapes,
  onExpandTools,
  onToolClick,
  onClearAll,
  onUseLandParcel,
  onCancelLandParcel
}) => {
  const drawLabel =
    activeTool !== 'selector'
      ? getToolInstruction(activeTool)
      : 'Draw your project area';

  return (
    <div className="byda-member-lookup__map-panel">
      {toolExpanded && (
        <>
          <div className="byda-member-lookup__map-panel-search-bg">
            <MapLocationSearch
              noOpenButton={true}
              onUseLandParcel={onUseLandParcel}
              onCancelLandParcel={onCancelLandParcel}
            />
            <div className="byda-member-lookup__map-panel-divider">OR</div>
          </div>
        </>
      )}

      {!toolExpanded && (
        <div
          className="byda-member-lookup__map-panel-expander"
          onClick={onExpandTools}
        >
          <Maximize16 />
        </div>
      )}

      <div className="byda-member-lookup__map-panel-draw">
        {toolExpanded && (
          <div className="byda-member-lookup__map-panel-draw-label">
            {drawLabel}
          </div>
        )}

        <div className="byda-member-lookup__map-panel-draw-buttons">
          <Button
            renderIcon={AreaCustom16}
            hasIconOnly
            kind="ghost"
            size="field"
            iconDescription="SHAPE"
            disabled={!isReady}
            className={`byda-member-lookup__map-panel-draw-tool${
              activeTool === 'polygon' ? ' active-map-tool' : ''
            }`}
            onClick={() => onToolClick('polygon')}
          />
          <Button
            renderIcon={FlowData16}
            hasIconOnly
            kind="ghost"
            size="field"
            iconDescription="LINE"
            disabled={!isReady}
            className={`byda-member-lookup__map-panel-draw-tool${
              activeTool === 'polyline' ? ' active-map-tool' : ''
            }`}
            onClick={() => onToolClick('polyline')}
          />
          <Button
            renderIcon={RadioButton16}
            hasIconOnly
            kind="ghost"
            size="field"
            iconDescription="CIRCLE"
            disabled={!isReady}
            className={`byda-member-lookup__map-panel-draw-tool${
              activeTool === 'circle' ? ' active-map-tool' : ''
            }`}
            onClick={() => onToolClick('circle')}
          />
          {hasDrawnShapes && (
            <Button
              kind="ghost"
              size="field"
              renderIcon={TrashCan16}
              iconDescription="Clear all"
              tooltipPosition="bottom"
              hasIconOnly
              onClick={onClearAll}
            />
          )}
        </div>

        {!toolExpanded && (
          <div className="byda-member-lookup__map-panel-draw-instruction">
            {getToolInstruction(activeTool)}
          </div>
        )}
      </div>
    </div>
  );
};

MemberLookupMapPanel.displayName = 'MemberLookupMapPanel';

export default React.memo(MemberLookupMapPanel);
